import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import debounce from "lodash.debounce";
import Card from "components/Card";
import { Label, Select, TagsInput, TextInput } from "components/Form";
import { Grid } from "components/Containers";
import { GET_ADMINS, GET_DISCIPLINES, GET_SKILLS } from "graphql/queries";
import { ROLE_STATES_OPTIONS } from "constants/index";
import { useAuth, useParamFunctions } from "hooks";
import { KEYS } from "components/Missions/Roles";

/**
 * Roles Filters
 *
 * @param {Boolean}   hideStatusFilter
 * @param {Boolean}   hideTalentOwnerFilter
 * @param {Boolean}   hideSalesOwnerFilter
 * @param {Boolean}   hideKeywordSearch
 * @param {Boolean}   hidePrimarySkillsFilter
 * @param {Boolean}   hideDisciplinesFilter
 */
const RolesFilters = ({
  hideStatusFilter = false,
  hideTalentOwnerFilter = false,
  hideSalesOwnerFilter = false,
  hideKeywordSearch = false,
  hidePrimarySkillsFilter = true,
  hideDisciplinesFilter = true,
}) => {
  const { paramParse, setParams } = useParamFunctions();
  const { domainId } = useAuth();
  const parsedFilters = paramParse(KEYS.filters);
  const {
    talentOwnerId,
    state,
    salesOwnerId,
    keyword: initialKeyword,
    primarySkills,
    disciplines,
  } = parsedFilters || {};
  const [keyword, setKeyword] = useState(initialKeyword);
  const [debounceKeyword, setDebounceKeyword] = useState(initialKeyword);

  const debouncedSetKeyword = useMemo(() => debounce((val) => setDebounceKeyword(val), 500), []);

  useEffect(() => {
    return () => {
      debouncedSetKeyword.cancel();
    };
  }, [debouncedSetKeyword]);

  useEffect(() => {
    setKeyword(initialKeyword);
  }, [initialKeyword]);

  const handleSearchInputChange = (_name, val) => {
    setKeyword(val);
    debouncedSetKeyword(val);
  };

  useEffect(() => {
    setParams(KEYS.filters, { ...parsedFilters, keyword: debounceKeyword }, true, true);
  }, [debounceKeyword]);

  const handleChange = (name, value) => setParams(KEYS.filters, { ...parsedFilters, [name]: value }, true, true);

  return (
    <Card>
      <Grid cols={4}>
        {!hideKeywordSearch && (
          <Grid.col>
            <TextInput
              name="keyword"
              value={keyword}
              placeholder="Search"
              onChange={handleSearchInputChange}
              type="search"
            />
          </Grid.col>
        )}
        {!hidePrimarySkillsFilter && (
          <Grid.col start={1} end={3}>
            <Label>
              <FormattedMessage id="Missions.Roles.RolesFilter.PrimarySkills" />
            </Label>
            <TagsInput
              name="primarySkills"
              queryName={GET_SKILLS}
              queryKeyName="skills"
              queryVariables={{ domainId }}
              minLength={0}
              onChange={handleChange}
              value={primarySkills}
              placeholder="Type to search..."
              labelKey="name"
              valueKey="id"
            />
          </Grid.col>
        )}
        {!hideDisciplinesFilter && (
          <Grid.col start={3} end={5}>
            <Label>
              <FormattedMessage id="Missions.Roles.RolesFilter.Disciplines" />
            </Label>
            <TagsInput
              name="disciplines"
              queryName={GET_DISCIPLINES}
              queryKeyName="disciplines"
              queryVariables={{ domainId }}
              minLength={0}
              value={disciplines}
              onChange={handleChange}
              placeholder="Type to search..."
              labelKey="name"
              valueKey="id"
            />
          </Grid.col>
        )}
        {!hideStatusFilter && (
          <Grid.col>
            <Select
              name="state"
              value={state}
              placeholder="Status"
              options={ROLE_STATES_OPTIONS}
              valueKey="value"
              labelKey="name"
              onChange={handleChange}
              isClearable
            />
          </Grid.col>
        )}
        {!hideTalentOwnerFilter && (
          <Grid.col>
            <Select
              name="talentOwnerId"
              value={talentOwnerId}
              placeholder="Talent Owner"
              labelKey="name"
              valueKey="id"
              queryName={GET_ADMINS}
              queryKeyName="admins"
              onChange={handleChange}
              isClearable
            />
          </Grid.col>
        )}
        {!hideSalesOwnerFilter && (
          <Grid.col>
            <Select
              name="salesOwnerId"
              value={salesOwnerId}
              placeholder="Sales Owner"
              labelKey="name"
              valueKey="id"
              queryName={GET_ADMINS}
              queryKeyName="admins"
              onChange={handleChange}
              isClearable
            />
          </Grid.col>
        )}
      </Grid>
    </Card>
  );
};

RolesFilters.propTypes = {
  hideStatusFilter: PropTypes.bool,
  hideTalentOwnerFilter: PropTypes.bool,
  hideSalesOwnerFilter: PropTypes.bool,
  hideKeywordSearch: PropTypes.bool,
  hidePrimarySkillsFilter: PropTypes.bool,
  hideDisciplinesFilter: PropTypes.bool,
};

export default RolesFilters;
